import React, { useState } from 'react';
import { View, FlatList, RefreshControl, TouchableOpacity, Dimensions } from 'react-native';
import { YStack, XStack, H2, H3, H4, SizableText, Card, Button, Input } from 'tamagui';
import { MaterialIcons } from '@expo/vector-icons';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import * as Animatable from 'react-native-animatable';
import DefaultScreen from '@components/Global/DefaultScreenView';
import { useQuery } from '@tanstack/react-query';
import useGlobalUser from '@hooks/getGlobalUser';
import apis from '@api/api';
import { useNavigation } from '@react-navigation/native';

const MeetingList: React.FC = () => {
  const theme = themeSelector();
  const navigation = useNavigation();
  const selectedId = useGlobalUser();
  const screenWidth = Dimensions.get('window').width;
  const isDesktop = screenWidth > 1024;

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFilter, setSelectedFilter] = useState<'all' | 'scheduled' | 'completed' | 'cancelled'>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  // Fetch meetings with pagination
  const { data: meetingsData, isLoading, error, refetch, isRefetching } = useQuery({
    queryKey: ['meetings', selectedId, currentPage, itemsPerPage],
    queryFn: async () => {
      console.log('🔍 Fetching meetings for selectedId:', selectedId);
      if (!selectedId) {
        console.warn('🔍 No selectedId available, skipping meetings fetch');
        return { data: { meetings: [], pagination: { totalCount: 0, currentPage: 1, totalPages: 1 } } };
      }

      try {
        const response = await apis.getRecentMeetings(itemsPerPage, currentPage);
        console.log('🔍 Meetings response:', response);
        return response;
      } catch (error) {
        console.error('🔍 Error fetching meetings:', error);
        return { data: { meetings: [], pagination: { totalCount: 0, currentPage: 1, totalPages: 1 } } };
      }
    },
    enabled: !!selectedId,
    retry: 2,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });

  const meetings = meetingsData?.data?.meetings || [];
  const pagination = meetingsData?.data?.pagination || { totalCount: 0, currentPage: 1, totalPages: 1 };

  // Filter meetings based on search and status
  const filteredMeetings = meetings.filter(meeting => {
    const matchesSearch = searchQuery === '' ||
      meeting.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      meeting.location?.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesFilter = selectedFilter === 'all' || meeting.status === selectedFilter;

    return matchesSearch && matchesFilter;
  });

  // Get status badge color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return {
          bg: theme ? "#d1fae5" : "#064e3b",
          text: theme ? "#059669" : "#10b981"
        };
      case 'scheduled':
        return {
          bg: theme ? "#dbeafe" : "#0c4a6e",
          text: theme ? "#0ea5e9" : "#38bdf8"
        };
      case 'cancelled':
        return {
          bg: theme ? "#fee2e2" : "#7f1d1d",
          text: theme ? "#dc2626" : "#f87171"
        };
      default:
        return {
          bg: theme ? "#fef3c7" : "#451a03",
          text: theme ? "#d97706" : "#fbbf24"
        };
    }
  };

  // Render individual meeting card
  const renderMeetingCard = ({ item: meeting, index }) => {
    const statusColors = getStatusColor(meeting.status);

    return (
      <Animatable.View
        animation="fadeInUp"
        duration={400}
        delay={index * 50}
        key={meeting._id}
      >
        <Card
          marginBottom="$4"
          padding="$5"
          borderRadius={16}
          borderWidth={1}
          borderColor={theme ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.1)"}
          backgroundColor={theme ? "#ffffff" : "#1e293b"}
          shadowColor={theme ? "#0f172a" : "#000000"}
          shadowOffset={{ width: 0, height: 8 }}
          shadowOpacity={theme ? 0.06 : 0.2}
          shadowRadius={16}
          elevation={6}
          pressStyle={{ scale: 0.98 }}
          onPress={() => {
            console.log('Meeting clicked:', meeting);
            // Navigate to meeting details or rejoin
            // navigation.navigate('MeetingDetails', { meetingId: meeting._id });
          }}
        >
          <YStack gap="$3">
            {/* Header: Title and Status */}
            <XStack alignItems="center" justifyContent="space-between">
              <YStack flex={1} marginRight="$3">
                <SizableText
                  color={theme ? "black" : "white"}
                  size="$6"
                  fontWeight="700"
                  numberOfLines={2}
                >
                  {meeting.title || 'Untitled Meeting'}
                </SizableText>
              </YStack>

              <View
                style={{
                  backgroundColor: statusColors.bg,
                  paddingHorizontal: 12,
                  paddingVertical: 6,
                  borderRadius: 8,
                  borderWidth: 1,
                  borderColor: statusColors.text
                }}
              >
                <SizableText
                  size="$3"
                  fontWeight="700"
                  color={statusColors.text}
                >
                  {meeting.status?.toUpperCase() || 'PENDING'}
                </SizableText>
              </View>
            </XStack>

            {/* Meeting Details */}
            <YStack gap="$2">
              {/* Date and Time */}
              {meeting.startTime && (
                <XStack alignItems="center" gap="$2">
                  <MaterialIcons
                    name="schedule"
                    size={18}
                    color={theme ? "#64748b" : "#94a3b8"}
                  />
                  <SizableText
                    color={theme ? "#64748b" : "#94a3b8"}
                    size="$4"
                  >
                    {new Date(meeting.startTime).toLocaleDateString('en-US', {
                      weekday: 'short',
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </SizableText>
                </XStack>
              )}

              {/* Duration */}
              {meeting.duration && (
                <XStack alignItems="center" gap="$2">
                  <MaterialIcons
                    name="timer"
                    size={18}
                    color={theme ? "#64748b" : "#94a3b8"}
                  />
                  <SizableText
                    color={theme ? "#64748b" : "#94a3b8"}
                    size="$4"
                  >
                    Duration: {meeting.duration} minutes
                  </SizableText>
                </XStack>
              )}

              {/* Participants */}
              {meeting.participants?.length > 0 && (
                <XStack alignItems="center" gap="$2">
                  <MaterialIcons
                    name="people"
                    size={18}
                    color={theme ? "#64748b" : "#94a3b8"}
                  />
                  <SizableText
                    color={theme ? "#64748b" : "#94a3b8"}
                    size="$4"
                  >
                    {meeting.participants.length} participant{meeting.participants.length !== 1 ? 's' : ''}
                  </SizableText>
                </XStack>
              )}

              {/* Location */}
              {meeting.location && (
                <XStack alignItems="center" gap="$2">
                  <MaterialIcons
                    name="location-on"
                    size={18}
                    color={theme ? "#64748b" : "#94a3b8"}
                  />
                  <SizableText
                    color={theme ? "#64748b" : "#94a3b8"}
                    size="$4"
                    numberOfLines={1}
                    flex={1}
                  >
                    {meeting.location}
                  </SizableText>
                </XStack>
              )}

              {/* Room ID */}
              {meeting.roomId && (
                <XStack alignItems="center" gap="$2">
                  <MaterialIcons
                    name="meeting-room"
                    size={18}
                    color={theme ? "#64748b" : "#94a3b8"}
                  />
                  <SizableText
                    color={theme ? "#64748b" : "#94a3b8"}
                    size="$3"
                    fontFamily="monospace"
                  >
                    Room: {meeting.roomId}
                  </SizableText>
                </XStack>
              )}
            </YStack>

            {/* Action Buttons */}
            <XStack gap="$2" marginTop="$2">
              {meeting.status === 'scheduled' && (
                <Button
                  flex={1}
                  backgroundColor={theme ? "#059669" : "#10b981"}
                  color="white"
                  borderRadius={12}
                  onPress={() => {
                    console.log('Join meeting:', meeting.roomId);
                    // Navigate back to Meet and join this meeting
                    // @ts-ignore - navigation params need to be properly typed
                    navigation.navigate('Meet', { roomId: meeting.roomId });
                  }}
                  shadowColor="#059669"
                  shadowOffset={{ width: 0, height: 4 }}
                  shadowOpacity={0.3}
                  shadowRadius={8}
                  elevation={6}
                  pressStyle={{ scale: 0.98 }}
                >
                  <XStack alignItems="center" gap="$2">
                    <MaterialIcons name="videocam" size={18} color="white" />
                    <SizableText color="white" fontWeight="600">
                      Join
                    </SizableText>
                  </XStack>
                </Button>
              )}

              <Button
                flex={1}
                backgroundColor={theme ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.1)"}
                color={theme ? "black" : "white"}
                borderRadius={12}
                borderWidth={1}
                borderColor={theme ? "rgba(0,0,0,0.1)" : "rgba(255,255,255,0.2)"}
                onPress={() => {
                  console.log('View details:', meeting._id);
                  // Navigate to meeting details
                  // navigation.navigate('MeetingDetails', { meetingId: meeting._id });
                }}
                pressStyle={{ scale: 0.98 }}
              >
                <XStack alignItems="center" gap="$2">
                  <MaterialIcons name="info" size={18} color={theme ? "black" : "white"} />
                  <SizableText color={theme ? "black" : "white"} fontWeight="600">
                    Details
                  </SizableText>
                </XStack>
              </Button>
            </XStack>
          </YStack>
        </Card>
      </Animatable.View>
    );
  };

  return (
    <DefaultScreen>
      <YStack flex={1} paddingHorizontal={isDesktop ? "$8" : "$4"} paddingVertical="$6">
        {/* Header */}
        <Animatable.View animation="fadeInDown" duration={800}>
          <XStack alignItems="center" marginBottom="$6">
            <TouchableOpacity onPress={() => navigation.goBack()}>
              <View style={{
                borderRadius: 12,
                padding: 12,
                marginRight: 16,
                borderWidth: 1,
                borderColor: theme ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.1)",
                backgroundColor: theme ? "#f8fafc" : "#334155"
              }}>
                <MaterialIcons
                  name="arrow-back"
                  size={24}
                  color={theme ? "black" : "white"}
                />
              </View>
            </TouchableOpacity>

            <YStack flex={1}>
              <H2 color={theme ? "black" : "white"}>Meeting History</H2>
              <SizableText color={theme ? "#64748b" : "#94a3b8"} size="$4">
                {pagination.totalCount} total meeting{pagination.totalCount !== 1 ? 's' : ''}
              </SizableText>
            </YStack>
          </XStack>
        </Animatable.View>

        {/* Search and Filters */}
        <Animatable.View animation="fadeInUp" duration={800} delay={200}>
          <YStack gap="$3" marginBottom="$5">
            {/* Search Bar */}
            <Input
              placeholder="Search meetings..."
              placeholderTextColor={theme ? "#94a3b8" : "#64748b"}
              value={searchQuery}
              onChangeText={setSearchQuery}
              size="$5"
              borderRadius={12}
              borderWidth={1}
              borderColor={theme ? "rgba(0,0,0,0.1)" : "rgba(255,255,255,0.1)"}
              backgroundColor={theme ? "#ffffff" : "#1e293b"}
              color={theme ? "black" : "white"}
              paddingLeft="$4"
            />

            {/* Filter Buttons */}
            <XStack gap="$2" flexWrap="wrap">
              {(['all', 'scheduled', 'completed', 'cancelled'] as const).map((filter) => (
                <Button
                  key={filter}
                  size="$3"
                  backgroundColor={selectedFilter === filter
                    ? (theme ? "#0ea5e9" : "#38bdf8")
                    : (theme ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.1)")
                  }
                  color={selectedFilter === filter ? "white" : (theme ? "black" : "white")}
                  onPress={() => setSelectedFilter(filter)}
                  borderRadius={8}
                  borderWidth={1}
                  borderColor={selectedFilter === filter
                    ? (theme ? "#0ea5e9" : "#38bdf8")
                    : (theme ? "rgba(0,0,0,0.1)" : "rgba(255,255,255,0.2)")
                  }
                  pressStyle={{ scale: 0.96 }}
                >
                  <SizableText
                    color={selectedFilter === filter ? "white" : (theme ? "black" : "white")}
                    fontWeight="600"
                  >
                    {filter.charAt(0).toUpperCase() + filter.slice(1)}
                  </SizableText>
                </Button>
              ))}
            </XStack>
          </YStack>
        </Animatable.View>

        {/* Meetings List */}
        <YStack flex={1}>
          {isLoading ? (
            <YStack alignItems="center" justifyContent="center" flex={1}>
              <MaterialIcons
                name="hourglass-empty"
                size={48}
                color={theme ? "#64748b" : "#94a3b8"}
                style={{ marginBottom: 16 }}
              />
              <SizableText
                size="$5"
                color={theme ? "black" : "white"}
                textAlign="center"
              >
                Loading meetings...
              </SizableText>
            </YStack>
          ) : error ? (
            <YStack alignItems="center" justifyContent="center" flex={1}>
              <MaterialIcons
                name="error-outline"
                size={48}
                color={theme ? "#dc2626" : "#f87171"}
                style={{ marginBottom: 16 }}
              />
              <SizableText
                size="$5"
                color={theme ? "#dc2626" : "#f87171"}
                textAlign="center"
                marginBottom="$2"
              >
                Failed to load meetings
              </SizableText>
              <SizableText
                size="$4"
                color={theme ? "#64748b" : "#94a3b8"}
                textAlign="center"
                marginBottom="$4"
              >
                {error?.message || 'Please try again later'}
              </SizableText>
              <Button
                backgroundColor={theme ? "#0ea5e9" : "#38bdf8"}
                color="white"
                onPress={() => refetch()}
                borderRadius={12}
                shadowColor="#0ea5e9"
                shadowOffset={{ width: 0, height: 4 }}
                shadowOpacity={0.3}
                shadowRadius={8}
                elevation={6}
                pressStyle={{ scale: 0.96 }}
              >
                <XStack alignItems="center" gap="$2">
                  <MaterialIcons name="refresh" size={20} color="white" />
                  <SizableText color="white" fontWeight="600">
                    Retry
                  </SizableText>
                </XStack>
              </Button>
            </YStack>
          ) : filteredMeetings.length === 0 ? (
            <YStack alignItems="center" justifyContent="center" flex={1}>
              <MaterialIcons
                name="video-call"
                size={64}
                color={theme ? "#64748b" : "#94a3b8"}
                style={{ marginBottom: 20 }}
              />
              <SizableText
                size="$6"
                color={theme ? "black" : "white"}
                textAlign="center"
                fontWeight="600"
                marginBottom="$2"
              >
                No meetings found
              </SizableText>
              <SizableText
                size="$4"
                color={theme ? "#64748b" : "#94a3b8"}
                textAlign="center"
                marginBottom="$4"
              >
                {searchQuery || selectedFilter !== 'all'
                  ? 'Try adjusting your search or filters'
                  : 'Create a new meeting to get started!'}
              </SizableText>
              <Button
                backgroundColor={theme ? "#0ea5e9" : "#38bdf8"}
                color="white"
                onPress={() => {
                  // @ts-ignore - navigation params need to be properly typed
                  navigation.navigate('Meet');
                }}
                borderRadius={12}
                shadowColor="#0ea5e9"
                shadowOffset={{ width: 0, height: 6 }}
                shadowOpacity={0.4}
                shadowRadius={12}
                elevation={8}
                pressStyle={{ scale: 0.96 }}
              >
                <XStack alignItems="center" gap="$2">
                  <MaterialIcons name="add" size={20} color="white" />
                  <SizableText color="white" fontWeight="600">
                    Create Meeting
                  </SizableText>
                </XStack>
              </Button>
            </YStack>
          ) : (
            <FlatList
              data={filteredMeetings}
              renderItem={renderMeetingCard}
              keyExtractor={(item) => item._id}
              showsVerticalScrollIndicator={false}
              refreshControl={
                <RefreshControl
                  refreshing={isRefetching}
                  onRefresh={refetch}
                  tintColor={theme ? "#0ea5e9" : "#38bdf8"}
                />
              }
              ListFooterComponent={
                pagination.totalPages > 1 ? (
                  <XStack justifyContent="center" alignItems="center" gap="$3" marginVertical="$4">
                    <Button
                      size="$3"
                      backgroundColor={theme ? "#0ea5e9" : "#38bdf8"}
                      color="white"
                      disabled={currentPage === 1}
                      onPress={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      opacity={currentPage === 1 ? 0.5 : 1}
                      borderRadius={8}
                      pressStyle={{ scale: 0.96 }}
                    >
                      <MaterialIcons name="chevron-left" size={20} color="white" />
                    </Button>

                    <SizableText color={theme ? "black" : "white"} fontWeight="600">
                      Page {currentPage} of {pagination.totalPages}
                    </SizableText>

                    <Button
                      size="$3"
                      backgroundColor={theme ? "#0ea5e9" : "#38bdf8"}
                      color="white"
                      disabled={currentPage === pagination.totalPages}
                      onPress={() => setCurrentPage(prev => Math.min(pagination.totalPages, prev + 1))}
                      opacity={currentPage === pagination.totalPages ? 0.5 : 1}
                      borderRadius={8}
                      pressStyle={{ scale: 0.96 }}
                    >
                      <MaterialIcons name="chevron-right" size={20} color="white" />
                    </Button>
                  </XStack>
                ) : null
              }
            />
          )}
        </YStack>
      </YStack>
    </DefaultScreen>
  );
};

export default MeetingList;